import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DashboardLayout, Card, Grid, CardIcon, CardTitle, CardValue, CardDescription, ActionButton } from '../../components/DashboardLayout';
import glassTheme from '../../styles/glassTheme';
import { dashboardApi, ContentManagerDashboardData } from '../../services/dashboardApi';

// This dashboard is only rendered on the /content-manager route tree (guarded
// to the content_manager role in App.tsx), which is a DIFFERENT route from
// /admin/content (admin/super_admin only). Deep-links from here must use the
// content-manager-scoped path or RoleRoute silently bounces the user back to
// this same dashboard, making a real navigation look like a no-op refetch.
const CONTENT_MANAGEMENT_PATH = '/content-manager/content';

const StatCard = styled(Card)`
  background: linear-gradient(135deg, rgba(0, 255, 136, 0.1) 0%, rgba(0, 200, 100, 0.1) 100%);
  border: 1px solid rgba(0, 255, 136, 0.3);
`;

const ContentItem = styled.div`
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all ${glassTheme.transition.base};

  &:hover {
    background: rgba(0, 0, 0, 0.3);
    transform: translateX(5px);
    cursor: pointer;
  }
`;

const CategoryStatRow = styled.div<{ $clickable?: boolean }>`
  padding: 0.5rem 0.75rem;
  margin: -0.5rem -0.75rem 0.75rem -0.75rem;
  border-radius: ${glassTheme.borderRadius.md};
  transition: background ${glassTheme.transition.base};
  cursor: ${props => props.$clickable === false ? 'default' : 'pointer'};

  &:hover {
    background: ${props => props.$clickable === false ? 'transparent' : 'rgba(255, 255, 255, 0.05)'};
  }
`;

const ContentIcon = styled.div<{ type: 'concept' | 'exercise' | 'example' }>`
  width: 40px;
  height: 40px;
  border-radius: ${glassTheme.borderRadius.md};
  background: ${props => {
    switch (props.type) {
      case 'concept': return 'rgba(0, 184, 255, 0.2)';
      case 'exercise': return 'rgba(255, 184, 0, 0.2)';
      case 'example': return 'rgba(0, 255, 136, 0.2)';
      default: return 'rgba(128, 128, 128, 0.2)';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const ContentDetails = styled.div`
  flex: 1;
`;

const ContentTitle = styled.div`
  color: ${glassTheme.colors.text.primary};
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const ContentMeta = styled.div`
  color: ${glassTheme.colors.text.muted};
  font-size: 0.85rem;
`;

const StatusBadge = styled.span<{ status: 'published' | 'draft' | 'archived' }>`
  padding: 0.25rem 0.75rem;
  border-radius: ${glassTheme.borderRadius.md};
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'published': return 'rgba(0, 255, 136, 0.2)';
      case 'draft': return 'rgba(255, 184, 0, 0.2)';
      case 'archived': return 'rgba(128, 128, 128, 0.2)';
      default: return 'rgba(128, 128, 128, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'published': return '#00FF88';
      case 'draft': return '#FFB800';
      case 'archived': return '#888888';
      default: return '#888888';
    }
  }};
`;

export const ContentManagerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<ContentManagerDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setData(await dashboardApi.getContentManager());
      } catch (err) {
        console.error('Failed to load content manager dashboard:', err);
        setError('无法加载内容数据，请稍后重试');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const stats = data?.stats;
  const recentContent = data?.recentlyEdited || [];

  const relativeTime = (iso: string) => {
    if (!iso) return '';
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 60) return `${Math.max(mins, 1)} 分钟前`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} 小时前`;
    return `${Math.floor(hours / 24)} 天前`;
  };

  const quickActions = [
    {
      icon: '📝',
      title: '创建知识元',
      description: '添加新的统计学知识元',
      onClick: () => navigate(CONTENT_MANAGEMENT_PATH, {
        state: { guideMessage: '请在左侧目录中选择分类，点击"+"新增知识元' }
      })
    },
    {
      icon: '📚',
      title: '内容库',
      description: `${stats ? stats.topics : 0} 个知识元`,
      onClick: () => navigate(CONTENT_MANAGEMENT_PATH)
    },
    {
      icon: '✅',
      title: '待发布',
      description: `${stats ? stats.drafts : 0} 条草稿`,
      onClick: () => navigate(CONTENT_MANAGEMENT_PATH, { state: { draftsOnly: true } })
    },
    {
      icon: '❓',
      title: '题库',
      description: `${stats ? stats.questions : 0} 道题目`,
      onClick: () => navigate(CONTENT_MANAGEMENT_PATH)
    }
  ];

  const getStatusText = (published: boolean) => (published ? '已发布' : '草稿');

  return (
    <DashboardLayout
      title="内容管理仪表盘"
      subtitle="创建和管理学习内容"
    >
      {error && (
        <Card style={{ marginBottom: '1.5rem' }}>
          <CardIcon>⚠️</CardIcon>
          <CardDescription>{error}</CardDescription>
        </Card>
      )}
      <Grid>
        <StatCard onClick={() => navigate(CONTENT_MANAGEMENT_PATH)} style={{ cursor: 'pointer' }}>
          <CardIcon>📖</CardIcon>
          <CardValue>{stats ? `${stats.published}/${stats.topics}` : '—'}</CardValue>
          <CardTitle>知识元</CardTitle>
          <CardDescription>已发布/总数 · 点击浏览内容库</CardDescription>
        </StatCard>

        <StatCard>
          <CardIcon>❓</CardIcon>
          <CardValue>{stats ? stats.questions : '—'}</CardValue>
          <CardTitle>题库</CardTitle>
          <CardDescription>题目总数</CardDescription>
        </StatCard>

        <StatCard
          onClick={() => navigate(CONTENT_MANAGEMENT_PATH, { state: { openCategoryModal: true } })}
          style={{ cursor: 'pointer' }}
        >
          <CardIcon>🗂️</CardIcon>
          <CardValue>{stats ? stats.categories : '—'}</CardValue>
          <CardTitle>分类</CardTitle>
          <CardDescription>主分类与子分类总数 · 点击新增分类</CardDescription>
        </StatCard>

        <StatCard
          onClick={() => navigate(CONTENT_MANAGEMENT_PATH, { state: { draftsOnly: true } })}
          style={{ cursor: 'pointer' }}
        >
          <CardIcon>📊</CardIcon>
          <CardValue>{stats ? stats.drafts : '—'}</CardValue>
          <CardTitle>待发布</CardTitle>
          <CardDescription>尚未发布的知识元 · 点击查看</CardDescription>
        </StatCard>

        {quickActions.map((action, index) => (
          <Card key={index} onClick={action.onClick} style={{ cursor: 'pointer' }}>
            <CardIcon>{action.icon}</CardIcon>
            <CardTitle>{action.title}</CardTitle>
            <CardDescription>{action.description}</CardDescription>
          </Card>
        ))}
      </Grid>

      <Card style={{ marginBottom: '2rem' }}>
        <CardTitle>最近编辑的内容</CardTitle>
        {loading ? (
          <CardDescription>加载中...</CardDescription>
        ) : recentContent.length === 0 ? (
          <CardDescription>暂无内容</CardDescription>
        ) : recentContent.map((item) => (
          <ContentItem
            key={item.slug}
            onClick={() => navigate(CONTENT_MANAGEMENT_PATH, { state: { focusTopicSlug: item.slug } })}
          >
            <ContentIcon type="concept">📖</ContentIcon>
            <ContentDetails>
              <ContentTitle>{item.title}</ContentTitle>
              <ContentMeta>知识元 • 更新于 {relativeTime(item.updatedAt)}</ContentMeta>
            </ContentDetails>
            <StatusBadge status={item.published ? 'published' : 'draft'}>
              {getStatusText(item.published)}
            </StatusBadge>
          </ContentItem>
        ))}
      </Card>

      <Grid>
        <Card>
          <CardTitle>内容分类统计</CardTitle>
          <div style={{ marginTop: '1rem' }}>
            {loading ? (
              <CardDescription>加载中...</CardDescription>
            ) : (data?.categories || []).map((c) => {
              const max = Math.max(...(data?.categories || []).map(x => x.topicsCount), 1);
              return (
                <CategoryStatRow
                  key={c.slug}
                  onClick={() => navigate(CONTENT_MANAGEMENT_PATH, {
                    state: { focusCategoryCode: c.code, focusCategoryName: c.name }
                  })}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span style={{ color: glassTheme.colors.text.primary }}>{c.name}</span>
                    <span style={{ color: glassTheme.colors.text.muted }}>{c.topicsCount} 个知识元</span>
                  </div>
                  <div style={{ width: '100%', height: '8px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px' }}>
                    <div style={{ width: `${Math.round((c.topicsCount / max) * 100)}%`, height: '100%', background: '#00B8FF', borderRadius: '4px' }}></div>
                  </div>
                </CategoryStatRow>
              );
            })}
          </div>
        </Card>

        {/* Item difficulty from real student responses - the most actionable
            authoring signal available. Each row links directly to the topic's
            exercise editor when the source topic can still be resolved. */}
        <Card>
          <CardTitle>最需要修订的练习</CardTitle>
          <CardDescription>按学生错误率排序（至少 3 次作答），点击直接前往编辑</CardDescription>
          <div style={{ marginTop: '1rem' }}>
            {loading ? (
              <CardDescription>加载中...</CardDescription>
            ) : (data?.hardestExercises || []).length === 0 ? (
              <CardDescription>暂无足够的作答数据</CardDescription>
            ) : (data?.hardestExercises || []).map((ex) => (
              <CategoryStatRow
                key={ex.exerciseId}
                $clickable={!!ex.topicId}
                onClick={() => {
                  if (!ex.topicId) return;
                  navigate(CONTENT_MANAGEMENT_PATH, {
                    state: {
                      focusTopicId: ex.topicId,
                      focusTopicSlug: ex.topicSlug,
                      focusTab: 'exercise'
                    }
                  });
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', fontSize: '0.85rem' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '65%', color: glassTheme.colors.text.primary }}>
                    {ex.topicTitle || ex.exerciseId}
                  </span>
                  <span style={{ color: glassTheme.colors.text.muted }}>错误率 {ex.failureRate}%（{ex.attempts} 次）</span>
                </div>
                <div style={{ width: '100%', height: '8px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px' }}>
                  <div style={{ width: `${ex.failureRate}%`, height: '100%', background: ex.failureRate >= 50 ? '#FF5A5A' : '#FFB800', borderRadius: '4px' }}></div>
                </div>
                {!ex.topicId && (
                  <div style={{ fontSize: '0.75rem', color: glassTheme.colors.text.muted, marginTop: '0.15rem' }}>
                    未关联知识元，无法直接跳转
                  </div>
                )}
              </CategoryStatRow>
            ))}
          </div>
        </Card>
      </Grid>
    </DashboardLayout>
  );
};
